import React, { useState, useEffect, useRef } from 'react';
import './PlayerSearch.css';

// Get the API base URL (same as api.js)
const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }
    if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:8000';
    }
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

const PlayerSearch = ({ onPlayerSelect, onSeasonChange, selectedSeason, selectedSeasonType }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const searchTimeoutRef = useRef(null);
    const inputRef = useRef(null);
    const isSearchingRef = useRef(false);

    // Debounced search
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (query.length >= 2) {
            searchTimeoutRef.current = setTimeout(() => {
                searchPlayers();
            }, 800); // Further increased delay to prevent typing interruptions
        } else {
            setResults([]);
            setShowResults(false);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [query]);

    // Remove aggressive focus monitoring - it interrupts typing

    const searchPlayers = async () => {
        // Prevent multiple simultaneous searches
        if (isSearchingRef.current) {
            return;
        }
        
        isSearchingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/dev/player-logs/search?query=${encodeURIComponent(query)}&limit=20`);
            
            if (!response.ok) {
                // Check if we're getting HTML instead of JSON (common in production)
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('text/html')) {
                    throw new Error('API server not available. Please check if the backend is running.');
                }
                throw new Error(`Search failed: ${response.statusText}`);
            }

            const data = await response.json();
            setResults(data.results || []);
            setShowResults(true);
            // Don't force focus - let user keep typing naturally
        } catch (err) {
            // Handle JSON parsing errors specifically
            if (err.name === 'SyntaxError' && err.message.includes('Unexpected token')) {
                setError('API server not available. Please check if the backend is running.');
            } else {
                setError(err.message);
            }
            console.error('Error searching players:', err);
        } finally {
            setLoading(false);
            isSearchingRef.current = false;
            // Don't force focus after loading - causes typing interruption
        }
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        
        // Clear any validation errors
        e.target.setCustomValidity('');
        
        // Only update if value actually changed
        if (newValue !== query) {
            setQuery(newValue);
        }
    };

    const handlePlayerClick = (player) => {
        onPlayerSelect(player);
        setQuery('');
        setResults([]);
        setShowResults(false);
        // Don't force focus after selection - let user control it naturally
    };

    const handleInputFocus = () => {
        if (results.length > 0) {
            setShowResults(true);
        }
    };

    const handleInputClick = () => {
        // Just show results if available, don't force focus
        if (results.length > 0) {
            setShowResults(true);
        }
    };

    const handleContainerClick = (e) => {
        // Only hide results if clicking outside the search area
        if (!e.target.closest('.search-input-container') && !e.target.closest('.search-results')) {
            setShowResults(false);
        }
    };

    const handleInputBlur = () => {
        // Prevent blur entirely - immediately refocus
        console.log('Input blur detected, refocusing...');
        setTimeout(() => {
            if (inputRef.current && document.activeElement !== inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    };

    const handleResultsMouseDown = (e) => {
        // Prevent blur when clicking on results
        e.preventDefault();
    };

    return (
        <div className="player-search-container" onClick={handleContainerClick}>
            <div className="search-header">
                <h2>Search for a Player</h2>
                <p>Enter a player name to view their game logs</p>
            </div>

            <form onSubmit={(e) => e.preventDefault()} noValidate onInvalid={(e) => e.preventDefault()}>
                <div className="search-input-container">
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onClick={handleInputClick}
                        placeholder="Search for a player (e.g., Stephen Curry, LeBron James)..."
                        className="search-input"
                        disabled={loading}
                        autoComplete="off"
                        spellCheck="false"
                        autoCorrect="off"
                        autoCapitalize="off"
                    />
                    {loading && <div className="search-spinner"></div>}
                </div>
                
                <div className="season-selector-container">
                    <div className="season-selector">
                        <label htmlFor="season-select">Season:</label>
                        <select 
                            id="season-select"
                            value={selectedSeason} 
                            onChange={(e) => onSeasonChange(e.target.value, selectedSeasonType)}
                            className="season-dropdown"
                        >
                            <option value="2024-25">2024-25</option>
                            <option value="2023-24">2023-24</option>
                            <option value="2022-23">2022-23</option>
                            <option value="2021-22">2021-22</option>
                            <option value="2020-21">2020-21</option>
                            <option value="2019-20">2019-20</option>
                            <option value="2018-19">2018-19</option>
                            <option value="2017-18">2017-18</option>
                            <option value="2016-17">2016-17</option>
                            <option value="2015-16">2015-16</option>
                            <option value="2014-15">2014-15</option>
                            <option value="2013-14">2013-14</option>
                            <option value="2012-13">2012-13</option>
                            <option value="2011-12">2011-12</option>
                            <option value="2010-11">2010-11</option>
                            <option value="2009-10">2009-10</option>
                            <option value="2008-09">2008-09</option>
                            <option value="2007-08">2007-08</option>
                        </select>
                    </div>
                    
                    <div className="season-type-selector">
                        <label htmlFor="season-type-select">Type:</label>
                        <select 
                            id="season-type-select"
                            value={selectedSeasonType} 
                            onChange={(e) => onSeasonChange(selectedSeason, e.target.value)}
                            className="season-dropdown"
                        >
                            <option value="Regular Season + Playoffs">Regular Season + Playoffs</option>
                            <option value="Playoffs">Playoffs</option>
                            <option value="Pre Season">Pre Season</option>
                        </select>
                    </div>
                </div>
            </form>

            {error && (
                <div className="search-error">
                    <p>{error}</p>
                </div>
            )}

            {showResults && results.length > 0 && (
                <div className="search-results" onMouseDown={handleResultsMouseDown}>
                    <h3>Results ({results.length})</h3>
                    <div className="results-list">
                        {results.map((player) => (
                            <div
                                key={player.id}
                                className="player-result"
                                onClick={() => handlePlayerClick(player)}
                            >
                                <div className="player-result-info">
                                    <h4>{player.name || 'Unknown Player'}</h4>
                                    {player.team && (
                                        <p className="player-team">
                                            {typeof player.team === 'string' 
                                                ? player.team 
                                                : typeof player.team === 'object' && player.team.full_name 
                                                    ? player.team.full_name 
                                                    : 'Unknown Team'
                                            }
                                        </p>
                                    )}
                                    {player.position && (
                                        <p className="player-position">
                                            {typeof player.position === 'string' 
                                                ? player.position 
                                                : typeof player.position === 'object' 
                                                    ? JSON.stringify(player.position) 
                                                    : String(player.position || 'Unknown Position')
                                            }
                                        </p>
                                    )}
                                </div>
                                <div className="player-status">
                                    {player.is_active ? (
                                        <span className="status-active">Active</span>
                                    ) : (
                                        <span className="status-inactive">Inactive</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showResults && results.length === 0 && !loading && query.length >= 2 && (
                <div className="no-results">
                    <p>No players found matching "{query}"</p>
                    <p>Try a different search term</p>
                </div>
            )}

            {query.length < 2 && query.length > 0 && (
                <div className="search-hint">
                    <p>Enter at least 2 characters to search</p>
                </div>
            )}
        </div>
    );
};

export default PlayerSearch; 